/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import * as appStrings from '@touchplan/strings';
import { ProjectMembersApi } from 'src/app/app-data/state/ProjectMembersApi';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { Project } from 'src/app/app-data/models/Project';
import { sortArray } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { of, Observable, combineLatest } from 'rxjs';
import { sortMembersByName, sortInvitedByEmail } from 'src/app/utils/array-util';
import { takeUntil, map, last, take } from 'rxjs/operators';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';

import { InvitedUser } from 'src/app/app-data/models/InvitedUser';
import { InvitedUsersApi } from 'src/app/app-data/state/InvitedUsersApi';
import { FbdbRequests } from 'src/app/services/fbdb-requests.service';
import { AppState } from 'src/app/services/app-state.service';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import {
	AbstractItemListEditorComponent,
	IListItemData,
	IListItemEventData,
	ItemType,
} from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { DefaultSplitDataRenderType, ICloudFunctionProjectMember, ProjectMember } from '@touchplan/common';
import { DefaultLoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { getAnalyticsDecoratedUserId } from 'src/app/plain-javascript/services/analytics/getAnalyticsDecoratedUserId';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { FirebaseError } from 'firebase/app';
import { ProjectFirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/project-cloud-functions.service';

export type Participant = ProjectMember | InvitedUser;

export interface IProjectMemberDialogData {
	projectId: string;
	ownerUserId: string;
	membersApi: ProjectMembersApi;
	invitedApi: InvitedUsersApi;
	rolesApi: RolesApi;
	isAdmin: boolean;
}
@Component({
	selector: 'project-member-dialog',
	templateUrl: './project-member-dialog.component.html',
	styleUrls: ['./project-member-dialog.component.scss'],
	providers: [LoggerServiceFactory.provide('ProjectMemberDialogComponent')],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectMemberDialogComponent
	extends AbstractItemListEditorComponent<Participant>
	implements OnInit, OnDestroy
{
	public membersApi: ProjectMembersApi;
	public invitedApi: InvitedUsersApi;
	public rolesApi: RolesApi;
	public itemList$: Observable<Participant[]>;

	public projectId: string;
	public ownerUserId: string;

	private itemList: Participant[];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IProjectMemberDialogData,
		protected dialogRef: MatDialogRef<ProjectMemberDialogComponent>,
		protected changeDetectorRef: ChangeDetectorRef,
		private projectFBCloudFunctionsService: ProjectFirebaseCloudFunctionsService,
		private dialog: MatDialog,
		private appState: AppState,
		private fbdbRequests: FbdbRequests,
		private logger: DefaultLoggerService,
		private bannerMessageHandler: BannerMessageHandler
	) {
		super(changeDetectorRef, dialogRef);

		this.membersApi = data.membersApi;
		this.invitedApi = data.invitedApi;
		this.rolesApi = data.rolesApi;
		this.isAdmin = data.isAdmin;
		this.projectId = data.projectId;
		this.ownerUserId = data.ownerUserId;

		this.self = this;
		this.itemType = {
			type: ItemType.Member,
			singularDisplay: this.strings.PROJECT_ITEM_MEMBER_SINGULAR,
			pluralDisplay: this.strings.PROJECT_ITEM_MEMBER_PLURAL,
			icon: 'people',
		};
	}

	public ngOnInit() {
		super.ngOnInit();

		// get the list of items
		this.getItemListObservable()
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((items) => {
				this.itemList = items;
			});
		this.itemList$ = this.getItemListObservable().pipe(takeUntil(this.shutdownSubject$));
	}

	public ngOnDestroy() {
		super.ngOnDestroy();
	}

	public completeAdd(emittedData: IListItemEventData<Participant>) {
		let success = true;
		if (emittedData.type === 'Save') {
			const formData = emittedData.data;
			const email = formData['email'];
			const roleId = formData['roleId'];
			const secondaryRoles = (formData['roles'] ?? []).reduce((a, b) => ((a[b] = b), a), {});
			secondaryRoles[roleId] = roleId;
			const data: ICloudFunctionProjectMember = formData['admin']
				? {
						accessLevel: 'Admin',
						roleId,
				  }
				: {
						accessLevel: 'General',
						roleId,
						secondaryRoles,
				  };

			this.projectFBCloudFunctionsService.addUserToProject(this.projectId, data, email).subscribe({
				next: (response) => {
					if (response.userAlreadyExist) {
						this.dialog.open(GenericDialogComponent, {
							data: {
								title: response.userAlreadyExist,
								yesButtonText: appStrings.UICONTROL_TEXT_OK,
							},
							disableClose: true,
						});
						success = false;
					} else if (response.userId) {
						//the analytics id for "the user you're inviting"
						const intercomUid = getAnalyticsDecoratedUserId(response.userId);
						this.sendUserInvite(false, { userId: intercomUid, email });
						//this.membersApi.addItem(data);
					} else {
						this.sendUserInvite(true, { email });
						//this.invitedApi.addItem(data);
					}
				},
				error: (error) => {
					if (
						error.message === 'User is not allowed to join this project' ||
						error.message === 'User is already a project member'
					) {
						this.bannerMessageHandler.displayErrorMessage<FirebaseError>(error, error.message);
					} else {
						this.bannerMessageHandler.displayErrorMessage<FirebaseError>(error);
					}
				},
			});
		}

		if (success) {
			if (emittedData.form) {
				emittedData.form.reset();
			}
		}

		return success;
	}

	private sendUserInvite(newUser: boolean, userDetails: { email?: string; userId?: string }) {
		const result$ = newUser
			? this.fbdbRequests.intercomInviteNewUser({
					inviteeEmail: userDetails.email,
					projectId: this.projectId,
					inviterUserId: this.appState.userSync.$id,
			  })
			: this.fbdbRequests.intercomInviteExistingUser({
					inviteeIntercomId: userDetails.userId,
					projectId: this.projectId,
					inviterUserId: this.appState.userSync.$id,
			  });

		// deliberately not using takeUntil, don't want this cleaned up until it's done
		result$.pipe(last()).subscribe({
			next: (val) => {
				if (this?.logger) {
					this.logger.log('invite request done', val);
				}

				if (!newUser) {
					analyticsEventsService.addProjectMemberUserExists(userDetails.userId, userDetails.email);
				} else {
					analyticsEventsService.addProjectMemberUserDoesNotExist(userDetails.email);
				}
			},
			error: (err) => {
				this.logger.log('fbdb error hit', err);
				Logging.warning(appStrings.FBDB_REQUEST_EMAIL_FAILED);
			},
		});
	}

	public completeEdit(emittedData: IListItemEventData<Participant>): boolean {
		if (emittedData.item instanceof ProjectMember) {
			return this.editProjectMember(emittedData.item, emittedData);
		} else {
			return this.editInvitedUser(emittedData.item, emittedData);
		}
	}

	public isOwner(member: Participant) {
		return member instanceof ProjectMember && member.view.user && member.view.user.$id === this.ownerUserId;
	}

	protected createItemListObservable(): Observable<any[]> {
		return combineLatest([
			this.membersApi.data.list$.pipe(
				map((memberMap) => Array.from(memberMap.values())),
				sortArray(of(sortMembersByName))
			),
			this.invitedApi.data.list$.pipe(
				map((userMap) => Array.from(userMap.values())),
				sortArray(of(sortInvitedByEmail))
			),
		]).pipe(
			map((arr) => {
				return arr[0].concat(arr[1]);
			})
		);
	}

	public getListItemData(item: Participant): IListItemData {
		if (item) {
			if (item instanceof ProjectMember) {
				const member: ProjectMember = item;
				return {
					leftLogo: {
						type: 'color',
						value: member.view && member.view.roleId && member.view.role ? member.view.role.view.color : null,
					},
					middleContent: [
						member.view.user ? member.view.user.firstName + ' ' + member.view.user.lastName : '',
						member.view.accessLevel === 'Admin'
							? this.isOwner(item)
								? this.strings.PROJECT_MEMBERS_ADMIN_OWNER
								: this.strings.PROJECT_MEMBERS_ADMIN
							: '',
					],
					rightSummary: {
						type: 'text',
						value:
							member.view.secondaryRoles || member.view.accessLevel === 'Admin'
								? this.getMemberRoleCount(member)
								: '',
					},
				};
			} else if (item instanceof InvitedUser) {
				const invited: InvitedUser = item;

				return {
					leftLogo: {
						type: 'color',
						value: invited.view.color ? invited.view.color : null,
					},
					middleContent: [
						invited.view.email,
						invited.view.accessLevel === 'Admin' ? this.strings.PROJECT_MEMBERS_ADMIN : '',
					],
					rightSummary: {
						type: 'text',
						value:
							invited.view.secondaryRoles || invited.view.accessLevel === 'Admin'
								? this.getMemberRoleCount(invited)
								: '',
					},
				};
			}
		}

		return null;
	}

	public doesItemMatchSearch(item: Participant, searchText: string): boolean {
		let match = false;
		if (item instanceof ProjectMember) {
			const member: ProjectMember = item;
			match = (member.view.user ? member.view.user.firstName + ' ' + member.view.user.lastName : '')
				.toLowerCase()
				.includes(searchText.toLowerCase());
		} else {
			const invited: InvitedUser = item;
			match = item.view.email.toLowerCase().includes(searchText.toLowerCase());
		}

		return match;
	}

	public getItemDisplayTitle(item): string {
		let result;
		const titles = {
			ProjectMember: this.strings.PROJECT_MEMBER_MEMBER_SECTION_TITLE,
			InvitedUser: this.strings.PROJECT_MEMBER_INVITED_SECTION_TITLE,
		};

		let lookingFor;
		let first;
		if (item instanceof ProjectMember) {
			lookingFor = 'ProjectMember';
			first = this.itemList.find((entry) => entry instanceof ProjectMember);
		} else {
			lookingFor = 'InvitedUser';
			first = this.itemList.find((entry) => entry instanceof InvitedUser);
		}

		if (first === item) {
			result = titles[lookingFor];
		}

		return result;
	}

	// only return non-empty for admin or user who has more than one role
	private getMemberRoleCount(member: Participant): string {
		let count = 1;
		if (!member.view.roleId) {
			count = 0;
		} else if (member.view.secondaryRoles) {
			const keys = Object.getOwnPropertyNames(member.view.secondaryRoles).length;
			if (member.view.accessLevel !== 'Admin') {
				if (keys > 1) {
					count = keys;
				} else {
					// for non-admin, do not display if user does not have at least 2 roles
					count = -1;
				}
			} else {
				count = keys;
			}
		}
		return count < 0 ? '' : count === 0 ? 'No Role' : count === 1 ? '1 Role' : count + ' Roles';
	}

	private editProjectMember(member: ProjectMember, emittedData: IListItemEventData<Participant>): boolean {
		const success = true;

		if (emittedData.type === 'Delete') {
			this.projectFBCloudFunctionsService
				.removeUserFromProject({
					userId: member.view.userId,
					projectId: this.projectId,
				})
				.subscribe({
					error: (err) => {
						this.logger.log('Error trying to remove user from project', err);
					},
				});
			analyticsEventsService.deletedAProjectMember(member.$id, this.appState.userSync.$id);
		} else if (emittedData.type === 'Save') {
			const formData = emittedData.data;
			const roleId = formData['roleId'];
			const secondaryRoles = (formData['roles'] ?? []).reduce((a, b) => ((a[b] = b), a), {});
			secondaryRoles[roleId] = roleId;
			const data = formData['admin']
				? {
						accessLevel: 'Admin',
						roleId,
						secondaryRoles: null,
				  }
				: {
						accessLevel: 'General',
						roleId,
						secondaryRoles,
				  };
			this.membersApi.localUpdate(member.$id, data);
			this.membersApi.persistData(member, DefaultSplitDataRenderType.database);
		}
		//this.itemInEdit = null;

		return success;
	}

	private editInvitedUser(invited: InvitedUser, emittedData: IListItemEventData<Participant>): boolean {
		let success = true;

		if (emittedData.type === 'Delete') {
			this.projectFBCloudFunctionsService
				.removeUserFromProject({ email: invited.view.email, projectId: this.projectId })
				.subscribe({
					error: (err) => {
						this.logger.log('Error trying to remove user from project', err);
					},
				});
		} else if (emittedData.type === 'Save') {
			const formData = emittedData.data;
			const email = invited.view.email;
			const roleId = formData['roleId'];
			const secondaryRoles = (formData['roles'] ?? []).reduce((a, b) => ((a[b] = b), a), {});
			secondaryRoles[roleId] = roleId;
			const data: ICloudFunctionProjectMember = formData['admin']
				? {
						accessLevel: 'Admin',
						roleId,
				  }
				: {
						accessLevel: 'General',
						roleId,
						secondaryRoles,
				  };

			this.projectFBCloudFunctionsService.updateInvitedUser(this.projectId, data, email).subscribe({
				error: (err) => {
					this.logger.log('Error trying to update invited user', err);
				},
			});
		} else if (emittedData.type === 'RESEND' && invited?.$id === emittedData.id) {
			// use email instead of id since id is modified email, dot replaced by comma for example
			this.sendUserInvite(true, { email: invited.view.email });
			success = false; // so panel still stays after
		}
		//this.itemInEdit = null;

		return success;
	}
}
